import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLocation } from '@/contexts/LocationContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { apiRequest } from '@/lib/fetchWithAuth';
import { UserProfile } from '@shared/types';

interface PerformanceProps { user: UserProfile | null }
interface PerformanceMetric {
  id: string;
  name: string;
  value: string | number | null;
  locationId?: string;
}

export default function Performance(_props: PerformanceProps) {
  const { currentOrganizationId } = useOrganization();
  const { selectedLocationIds, locations } = useLocation();
  const { data = [], isLoading, isError } = useQuery<PerformanceMetric[]>({
    queryKey: ['performance-metrics', currentOrganizationId],
    queryFn: () => apiRequest(`/api/metrics?organizationId=${encodeURIComponent(currentOrganizationId!)}`),
    enabled: Boolean(currentOrganizationId),
  });
  const selected = new Set(selectedLocationIds.includes('all')
    ? locations.map(location => location.id) : selectedLocationIds);
  const metrics = data.filter(metric => metric.locationId && selected.has(metric.locationId));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Performance</h1>
        <p className="text-sm text-slate-600">Metrics for the selected organization and locations.</p>
      </div>
      {isLoading ? <p role="status">Loading performance data…</p> : isError ? (
        <p role="alert">Performance data could not be loaded. Please try again.</p>
      ) : metrics.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-slate-600">No performance data for the selected locations.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {metrics.map(metric => (
            <Card key={metric.id}>
              <CardHeader>
                <CardTitle className="text-base">{metric.name}</CardTitle>
                <p className="text-sm text-slate-600">{locations.find(location => location.id === metric.locationId)?.name}</p>
              </CardHeader>
              <CardContent className="text-2xl font-semibold">{metric.value ?? '—'}</CardContent>
            </Card>
          ))}
        </div>
      )}
      <p className="text-sm text-slate-600">Trends, location comparisons and recommendations will appear when verified reporting data is available.</p>
    </div>
  );
}
